import React from "react"
import { NavigationContext } from "./navigation-context"
import { NavigationAccountTrigger } from "./navigation-account-trigger"
import { NavigationFooter } from "./navigation-footer"
import { NavigationFooterCallout } from "./navigation-footer-callout"
import { NavigationGroup } from "./navigation-group"
import { NavigationGroupItem } from "./navigation-group-item"
import { NavigationHeader } from "./navigation-header"
import { NavigationItem } from "./navigation-item"
import { NavigationMenu } from "./navigation-menu"
import { NavigationPrimaryAction } from "./navigation-primary-action"
import { NavigationSidebar } from "./navigation-sidebar"
import { NavigationDock } from "./navigation-dock"
import { NavigationTrigger } from "./navigation-trigger"
import { useCollapsibleSidebar } from "./use-collapsible-sidebar"
import { NavigationHeaderCollapsibleContent } from "./navigation-header-collapsible-content"
import { NavigationLogoLink } from "./navigation-logo-link"

export { useNavigationContext } from "./navigation-context"
export type { NavigationContextValue } from "./navigation-context"
export type { NavigationAccountTriggerProps } from "./navigation-account-trigger"
export type { NavigationFooterProps } from "./navigation-footer"
export type { NavigationFooterCalloutProps } from "./navigation-footer-callout"
export type { NavigationGroupProps } from "./navigation-group"
export type { NavigationGroupItemProps } from "./navigation-group-item"
export type { NavigationHeaderProps } from "./navigation-header"
export type { NavigationItemProps } from "./navigation-item"
export type { NavigationMenuProps } from "./navigation-menu"
export type { NavigationDockProps } from "./navigation-dock"
export type { NavigationPrimaryActionProps } from "./navigation-primary-action"
export type { NavigationSidebarProps } from "./navigation-sidebar"
export type { NavigationTriggerProps } from "./navigation-trigger"

// =============================================================================
// Props
// =============================================================================

export type NavigationProps = {
  children?: React.ReactNode
  /** Whether the sidebar is collapsed (controlled) */
  collapsed?: boolean
  /** Initial collapsed state (uncontrolled) */
  defaultCollapsed?: boolean
  /**
   * Forces the sidebar collapsed while `true`, overriding the stored preference
   * without writing to it. Entering this state (transition from `false`) always
   * re-collapses; the user can still expand manually, and their toggles persist
   * as normal. Leaving it (`true` → `false`) reverts to the stored preference.
   * Use for routes that should always open with a collapsed nav (e.g. editors).
   */
  startCollapsed?: boolean
  /** Called when the collapsed state changes, and once on mount with the initial state. */
  onCollapsedChange?: (collapsed: boolean) => void
  /**
   * Current pathname (e.g. `window.location.pathname`). Items use this to
   * derive their own active state via `startsWith` matching against their
   * `value` prop.
   */
  currentPath?: string
  /**
   * Enables mobile layout mode: the sidebar hides completely when collapsed
   * (instead of collapsing to icon-only), and `Navigation.Dock` is visible
   * only while the sidebar is closed.
   */
  mobile?: boolean
  /**
   * Whether the current account is on a paid plan. When `"paid"`, nav rows
   * suppress the `"paid"` badge icon and its tooltip callout. Defaults to
   * `"free"`.
   */
  accountType?: "paid" | "free"
}

// =============================================================================
// Root
// =============================================================================

/**
 * Root provider for the navigation system. Always wraps `Navigation.Sidebar`
 * and, on mobile, `Navigation.Dock`. Owns collapsed state and distributes it
 * to all children via context.
 *
 * @example
 * ```tsx
 * // Desktop
 * <Navigation currentPath={currentPath}>
 *   <Navigation.Sidebar>
 *     <Navigation.Header>...</Navigation.Header>
 *     <Navigation.Menu>...</Navigation.Menu>
 *   </Navigation.Sidebar>
 * </Navigation>
 *
 * // Mobile (sidebar + dock share state)
 * <Navigation mobile currentPath={currentPath}>
 *   <Navigation.Sidebar>...</Navigation.Sidebar>
 *   <Navigation.Dock>...</Navigation.Dock>
 * </Navigation>
 * ```
 */
function NavigationRoot({
  children,
  collapsed,
  defaultCollapsed = false,
  startCollapsed = false,
  onCollapsedChange,
  currentPath = "",
  mobile = false,
  accountType = "free"
}: NavigationProps) {
  const { collapsed: isCollapsed, setCollapsed } = useCollapsibleSidebar({
    collapsed,
    defaultCollapsed,
    startCollapsed,
    onCollapsedChange
  })

  return (
    <NavigationContext.Provider
      value={{
        collapsed: isCollapsed,
        setCollapsed,
        currentPath,
        mobile,
        accountType
      }}
    >
      {children}
    </NavigationContext.Provider>
  )
}

NavigationRoot.displayName = "Navigation"

// =============================================================================
// Compound component
// =============================================================================

interface NavigationComponent {
  (props: NavigationProps): React.JSX.Element
  AccountTrigger: typeof NavigationAccountTrigger
  Dock: typeof NavigationDock
  Footer: typeof NavigationFooter
  FooterCallout: typeof NavigationFooterCallout
  Group: typeof NavigationGroup
  GroupItem: typeof NavigationGroupItem
  Header: typeof NavigationHeader
  HeaderCollapsibleContent: typeof NavigationHeaderCollapsibleContent
  Item: typeof NavigationItem
  LogoLink: typeof NavigationLogoLink
  Menu: typeof NavigationMenu
  PrimaryAction: typeof NavigationPrimaryAction
  Sidebar: typeof NavigationSidebar
  Trigger: typeof NavigationTrigger
}

export const Navigation: NavigationComponent = Object.assign(NavigationRoot, {
  AccountTrigger: NavigationAccountTrigger,
  Dock: NavigationDock,
  Footer: NavigationFooter,
  FooterCallout: NavigationFooterCallout,
  Group: NavigationGroup,
  GroupItem: NavigationGroupItem,
  Header: NavigationHeader,
  HeaderCollapsibleContent: NavigationHeaderCollapsibleContent,
  Item: NavigationItem,
  LogoLink: NavigationLogoLink,
  Menu: NavigationMenu,
  PrimaryAction: NavigationPrimaryAction,
  Sidebar: NavigationSidebar,
  Trigger: NavigationTrigger
} as const)
